import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SimpleEditor } from "@/components/tiptap-templates/simple/simple-editor";
import { usePostService } from "../services/postService";
import { useLanguage } from "../providers/languageProvider";
import {
  PostCategory,
  UpdatePostWithTranslationsData,
  PostWithCategories,
  LanguageCode,
} from "../types/post";

interface PostFormData {
  title: string;
  subtitleTranslations: Record<LanguageCode, string>;
  content: string;
  is_public: boolean;
  category_ids: string[];
}

export const EditPostPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const postService = usePostService();
  const { availableLanguages } = useLanguage();

  const [formData, setFormData] = useState<PostFormData>({
    title: "",
    subtitleTranslations: { en: "", pl: "", uk: "" },
    content: "",
    is_public: false,
    category_ids: [],
  });

  const [editorHtml, setEditorHtml] = useState<string>("");
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [post, setPost] = useState<PostWithCategories | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      const [categoriesData, postData] = await Promise.all([
        postService.getPostCategories(),
        postService.getPostWithTranslations(id!),
      ]);

      setCategories(categoriesData);

      // Check if post was found
      if (!postData) {
        alert("Post nie został znaleziony");
        navigate("/post");
        return;
      }

      setPost(postData);

      // Match category names to IDs
      const matchedCategoryIds = categoriesData
        .filter((cat) => cat.name && postData.category_names.includes(cat.name))
        .map((cat) => cat.id);

      const initialFormData = {
        title: postData.title,
        subtitleTranslations: postData.subtitleTranslations || {
          en: "",
          pl: "",
          uk: "",
        },
        content: postData.content || "",
        is_public: postData.is_public,
        category_ids: matchedCategoryIds,
      };

      setFormData(initialFormData);
      setEditorHtml(postData.content || "");
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Nie udało się załadować danych");
      navigate("/post");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setSubmitting(true);
      const submitData: UpdatePostWithTranslationsData = {
        title: formData.title,
        subtitleTranslations: formData.subtitleTranslations,
        content: editorHtml,
        is_public: formData.is_public,
        category_ids: formData.category_ids,
      };
      await postService.updatePostWithTranslations(id, submitData);
      navigate("/post");
    } catch (error) {
      console.error("Error updating post:", error);
      alert("Nie udało się zaktualizować postu");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/post");
  };

  const updateSubtitleTranslation = (language: LanguageCode, value: string) => {
    setFormData((prev) => ({
      ...prev,
      subtitleTranslations: { ...prev.subtitleTranslations, [language]: value },
    }));
  };

  if (loading) {
    return <div className="p-6 text-center">Ładowanie postu...</div>;
  }

  if (!post) {
    return <div className="p-6 text-center">Post nie został znaleziony.</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="m-0 mb-2 text-blue-500 text-2xl font-bold">
          Edytuj Post: {post.title}
        </h1>
        <button
          onClick={handleCancel}
          className="px-3 py-1.5 bg-gray-100 text-gray-600 border border-gray-300 rounded cursor-pointer text-sm hover:bg-gray-200 transition-colors"
        >
          ← Powrót do listy postów
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block mb-2 font-medium text-base">Tytuł:</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              className="w-full p-3 border border-gray-300 rounded text-base box-border focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="mb-6">
            <label className="block mb-3 font-medium text-base">
              Podtytuł (w różnych językach):
            </label>
            {availableLanguages.map((lang) => (
              <div key={`subtitle-${lang.code}`} className="mb-3">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  {lang.flag} {lang.name}
                </label>
                <input
                  type="text"
                  value={formData.subtitleTranslations[lang.code]}
                  onChange={(e) =>
                    updateSubtitleTranslation(lang.code, e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded text-base box-border focus:border-blue-500 focus:outline-none"
                  placeholder={`Wprowadź podtytuł w języku ${lang.name.toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-base">Treść:</label>
            <div className="border border-gray-300 rounded min-h-[400px]">
              <SimpleEditor
                initialContent={editorHtml}
                onChange={(html) => {
                  setEditorHtml(html);
                  setFormData((prev) => ({ ...prev, content: html }));
                }}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-base">
              Kategorie:
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <label
                  key={category.id}
                  className={`inline-flex items-center px-4 py-2 rounded-full cursor-pointer text-white text-sm font-medium transition-colors ${
                    formData.category_ids.includes(category.id)
                      ? "bg-blue-500"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.category_ids.includes(category.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          category_ids: [...formData.category_ids, category.id],
                        });
                      } else {
                        setFormData({
                          ...formData,
                          category_ids: formData.category_ids.filter(
                            (id) => id !== category.id
                          ),
                        });
                      }
                    }}
                    className="sr-only"
                  />
                  <span className="m-0">{category.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className={`px-6 py-3 text-white border-0 rounded-md text-base font-medium transition-colors ${
                submitting
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-500 cursor-pointer hover:bg-blue-600"
              }`}
            >
              {submitting ? "Aktualizowanie..." : "Zaktualizuj Post"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 bg-gray-300 text-gray-700 border-0 rounded-md text-base font-medium cursor-pointer hover:bg-gray-400 transition-colors"
            >
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
